import { createAsyncThunk, createSelector, createSlice, PayloadAction, SerializedError } from "@reduxjs/toolkit";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { RootState } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import MessagingAPIService from "messaging/_api/messaging-api-service";
import ForumQuery, { ForumFilterParameters, ForumOrderParameters } from "messaging/forums/_api/queries/forum-query";
import Forum from "messaging/forums/forum";
import ForumEntityClass from "notifications/values/forum-entity-class/forum-entity-class";
import Session from "users/__session/session";
import { publishComments, saveComments } from "work/proposals/comments/_store/comments-redux-slice";
import { CommentThreadJSON } from "work/proposals/comments/threads/_schemas/comment-thread-schema";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import Proposal from "work/proposals/proposal";

type CommentThreadsStoreState = {
  byProposalId: {
    entries: Record<string, CommentThreadJSON[]>;
    loading: Record<string, boolean>;
    error: Record<string, SerializedError | null>;
  };
};

const initialState: CommentThreadsStoreState = {
  byProposalId: {
    entries: {},
    loading: {},
    error: {},
  },
};
export const populateCommentThreadsByProposal = createAsyncThunk(
  "commentThreads/getCommentThreadsByProposal",
  async ({ session, proposal, proposalId }: { session: Session; proposal?: Proposal; proposalId?: Guid }, thunkAPI) => {
    const id: Guid | undefined = proposal?.id ?? proposalId;
    if (!id) return thunkAPI.rejectWithValue("Proposal id is required");
    try {
      const filterParameters = new ForumFilterParameters({
        entityClass: new ForumEntityClass("Work.Proposal"),
        entityId: id,
      });
      const orderParameters = new ForumOrderParameters({});
      const paginationParameters = PaginationParameters.create({ pageIndex: 0, pageSize: 25 });
      const request = new ForumQuery({
        filterParameters,
        orderParameters,
        paginationParameters,
      });
      const apiService = new MessagingAPIService(session);
      const forumJSON = await apiService.getForums(request);
      const forums = Forum.fromJSONArray(forumJSON) ?? [];
      return forums.map((forum) => CommentThread.fromForum(forum).toJSON());
    } catch (error) {
      console.error(error);
      return thunkAPI.rejectWithValue(error);
    }
  },
);
const commentThreadsSlice = createSlice({
  name: "CommentThreads",
  initialState,
  reducers: {
    addCommentThread: (state, action: PayloadAction<CommentThread>) => {
      if (action.payload.id) {
        const existingThreads = state.byProposalId.entries[action.payload.proposalId.value] ?? [];
        const newThreads = existingThreads.filter((thread) => thread.id !== action.payload.id?.value);
        newThreads.push(action.payload.toJSON());
        state.byProposalId.entries[action.payload.proposalId.value] = newThreads;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(populateCommentThreadsByProposal.pending, (state, action) => {
      const proposalId = action.meta.arg.proposal?.id?.value ?? action.meta.arg.proposalId?.value;
      if (!proposalId) {
        console.warn("Proposal id not referenced in populateCommentThreadsByProposal");
        return;
      }
      state.byProposalId.loading[proposalId] = true;
      state.byProposalId.error[proposalId] = null;
    });
    builder.addCase(populateCommentThreadsByProposal.fulfilled, (state, action) => {
      const proposalIdValue = action.meta.arg.proposalId?.value ?? action.meta.arg.proposal?.id?.value;
      if (!proposalIdValue) throw new Error("No proposal id provided for populating threads");

      state.byProposalId.loading[proposalIdValue] = false;
      state.byProposalId.entries[proposalIdValue] = action.payload;
    });
    builder.addCase(populateCommentThreadsByProposal.rejected, (state, action) => {
      const proposalId = action.meta.arg.proposal?.id?.value;
      if (!proposalId) {
        console.warn("Proposal id not referenced in populateCommentThreadsByProposal");
        return;
      }
      state.byProposalId.loading[proposalId] = false;
      state.byProposalId.error[proposalId] = action.error;
    });
    builder.addCase(saveComments.fulfilled, (state, action) => {
      const newComments = action.payload;
      if (!newComments.length) {
        return;
      }
      const newCommentsThreads = action.payload.map((comment) => comment.thread);
      for (const thread of newCommentsThreads) {
        const existingThreads = state.byProposalId.entries[thread.proposalId.value] ?? ([] as CommentThread[]);
        const newThreads = existingThreads.filter((t) => t.id !== thread.id?.value);
        newThreads.push(thread.toJSON());
        state.byProposalId.entries[thread.proposalId.value] = newThreads;
      }
    });
    builder.addCase(publishComments.fulfilled, (state, action) => {
      const newComments = action.payload;
      if (!newComments.length) {
        return;
      }
      const newCommentsThreads = action.payload.map((comment) => comment.thread);
      for (const thread of newCommentsThreads) {
        const existingThreads = state.byProposalId.entries[thread.proposalId.value] ?? [];
        const newThreads = existingThreads.filter((t) => t.id !== thread.id?.value);
        newThreads.push(thread.toJSON());
        state.byProposalId.entries[thread.proposalId.value] = newThreads;
      }
    });
  },
});

export const { addCommentThread } = commentThreadsSlice.actions;

const selectCommentThreadsByProposalId = createSelector(
  [
    (state: CommentThreadsStoreState) => state.byProposalId.entries,
    (_: CommentThreadsStoreState, proposalId: Guid | null | undefined) => proposalId,
  ],
  (entries, proposalId) => {
    if (!proposalId) return;
    return entries[proposalId.value]
      ?.map((threadJson) => CommentThread.fromJSON(threadJson))
      .filter((thread): thread is CommentThread => thread !== null);
  },
);
export const getCommentThreadsByProposal =
  (proposal?: Proposal | null, proposalId?: Guid | null) => (state: RootState) =>
    selectCommentThreadsByProposalId(state.commentThreads, proposal?.id ?? proposalId);

export const getIsLoadingCommentThreadsByProposal = (proposal?: Proposal | null, proposalId?: Guid | null) => {
  return (state: RootState) => {
    const id = proposal?.id?.value ?? proposalId?.value;
    if (!id) return false;
    return state.commentThreads?.byProposalId.loading[id];
  };
};
export const getErrorLoadingCommentThreadsByProposal = (proposal?: Proposal | null, proposalId?: Guid | null) => {
  return (state: RootState) => {
    const id = proposal?.id?.value ?? proposalId?.value;
    if (!id) return undefined;
    return state.commentThreads?.byProposalId.error[id];
  };
};

export default commentThreadsSlice;
